import { useState, useEffect } from "react";
import { useLocation, useSearch } from "wouter";
import { useUser, useAuth } from "@clerk/react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useOrg } from "@/lib/orgContext";
import {
  Trophy, CheckCircle2, Building2, ChevronRight, Zap, Shield, Star,
  Users, FileText, BarChart3, ArrowRight,
} from "lucide-react";

const DIVISIONS = ["Division I (FBS)", "Division I (FCS)", "Division I (Other)", "Division II", "Division III", "NAIA", "NJCAA", "Other"];

type PlanId = "free" | "track" | "draft" | "enterprise";

// Sales-led tier display (aligned with landing.tsx TIER_DISPLAY after
// PR #63). Dollar amounts intentionally absent — paid-tier pricing is
// shared on a scoping call so we can right-size by roster, sport mix,
// and integration needs. Only the Free trial is self-serve from this
// screen; selecting Command/Intelligence/Enterprise routes the user to
// the Talk-to-Sales flow instead of attempting a Stripe Checkout that
// would 500 without configured price IDs.
const PLANS: Array<{
  id: PlanId;
  name: string;
  priceLabel: string;
  description: string;
  highlight: boolean;
  badge: string | null;
  features: string[];
  icon: typeof Trophy;
}> = [
  {
    id: "free",
    name: "Free trial",
    priceLabel: "14 days free",
    description: "Read-only repository for up to 5 coaches — no card required.",
    highlight: false,
    badge: null,
    features: [
      "Up to 5 coaches (read-only)",
      "Contract repository",
      "Insurance & compliance tracking",
      "Expiration alerts",
      "Self-serve setup",
    ],
    icon: Trophy,
  },
  {
    id: "track",
    name: "Command",
    priceLabel: "Contact for pricing",
    description: "Mid-major and D-II departments managing a full coaching staff.",
    highlight: false,
    badge: null,
    features: [
      "No roster cap",
      "Negotiation rounds with side-by-side diff",
      "Approval routing by sport, role, or dollar threshold",
      "Live obligations & buyout exposure",
      "Unlimited e-signature",
    ],
    icon: Zap,
  },
  {
    id: "draft",
    name: "Intelligence",
    priceLabel: "Contact for pricing",
    description: "Power Five and large multi-sport athletic departments.",
    highlight: true,
    badge: "Most Popular",
    features: [
      "Everything in Command",
      "Renewal pipeline + 90/60/30 forecasting",
      "Custom approval policies per sport",
      "Curated clause library",
      "SSO (Okta, Azure AD, Google)",
    ],
    icon: Star,
  },
  {
    id: "enterprise",
    name: "Enterprise Plus",
    priceLabel: "Custom",
    description: "Conferences and multi-school athletic systems.",
    highlight: false,
    badge: null,
    features: [
      "Everything in Intelligence",
      "Conference-wide workspaces",
      "Custom HRIS / ERP / finance integrations",
      "Dedicated success manager + named SLA",
      "Single-tenant deployment option",
    ],
    icon: Shield,
  },
];

const STEPS = [
  { number: 1, label: "Institution" },
  { number: 2, label: "Plan" },
  { number: 3, label: "Done" },
];

export default function Onboarding() {
  const { user } = useUser();
  const { getToken } = useAuth();
  const { refetch } = useOrg();
  const [, setLocation] = useLocation();
  const search = useSearch();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [institutionName, setInstitutionName] = useState("");
  const [division, setDivision] = useState("");
  const [conference, setConference] = useState("");
  const [selectedPlan, setSelectedPlan] = useState<PlanId>("free");

  useEffect(() => {
    const params = new URLSearchParams(search);
    const plan = params.get("plan");
    if (plan === "free" || plan === "track" || plan === "draft" || plan === "enterprise") {
      setSelectedPlan(plan);
    }
  }, [search]);

  const handleProvision = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch("/api/auth/org/provision", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          institutionName,
          division,
          conference,
          plan: selectedPlan,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `Server error ${res.status}`);
      }
      await refetch();

      // Sales-led billing (PR #63/#65): paid tiers do NOT self-serve
      // through Stripe Checkout from this screen. Sessions started via a
      // checkout link the sales team generated still link the
      // subscription to the org via /billing/checkout/link. New
      // signups that pick a paid tier are routed to a sales-handoff
      // confirmation — the org is provisioned on the Free trial so they
      // can keep poking around while sales follows up.
      if (selectedPlan === "track" || selectedPlan === "draft" || selectedPlan === "enterprise") {
        const params = new URLSearchParams(search);
        const sessionId = params.get("session_id");

        if (sessionId) {
          try {
            const billingToken = await getToken();
            await fetch("/api/billing/checkout/link", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${billingToken}`,
              },
              body: JSON.stringify({ sessionId }),
            });
          } catch {
            // Best-effort link — fall through to confirmation either way
          }
        }
      }

      setStep(3);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleStep1Next = () => {
    if (!institutionName.trim()) return;
    setStep(2);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 relative overflow-hidden">
      {/* Ambient brand gradient — works in light & dark */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          backgroundImage:
            "radial-gradient(60% 40% at 50% 0%, hsl(var(--gold) / 0.10), transparent 70%), radial-gradient(50% 35% at 50% 100%, hsl(var(--navy) / 0.10), transparent 70%)",
        }}
      />

      <div className="w-full max-w-3xl relative">
        {/* Brand mark */}
        <div className="flex items-center justify-center gap-3 mb-10">
          <img src="/logo.svg" alt="Zindeal" className="w-11 h-11 rounded-full shadow-lg dark:hidden" />
          <img src="/logo-dark.svg" alt="Zindeal" className="w-11 h-11 rounded-full shadow-lg hidden dark:block" />
          <span className="font-serif font-semibold text-2xl tracking-tight text-foreground">Zindeal</span>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-center mb-10 gap-0">
          {STEPS.map((s, i) => {
            const done = step > s.number;
            const active = step === s.number;
            return (
              <div key={s.number} className="flex items-center">
                <div className="flex flex-col items-center gap-2">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ring-1 ${
                      done
                        ? "bg-field-green text-field-green-foreground ring-field-green/40 shadow-sm"
                        : active
                        ? "bg-gold text-gold-foreground ring-gold/50 shadow-md"
                        : "bg-muted text-muted-foreground ring-border"
                    }`}
                  >
                    {done ? <CheckCircle2 className="h-4 w-4" /> : s.number}
                  </div>
                  <span
                    className={`text-xs font-semibold uppercase tracking-wide ${
                      active ? "text-foreground" : done ? "text-field-green" : "text-muted-foreground"
                    }`}
                  >
                    {s.label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div
                    className={`w-20 h-px mx-3 mb-6 transition-colors ${
                      done ? "bg-field-green/60" : "bg-border"
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>

        {step === 1 && (
          <Card className="shadow-xl border border-card-border bg-card animate-fade-in">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-lg bg-gold-subtle ring-1 ring-gold/30 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-gold" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-card-foreground">
                    Welcome{user?.firstName ? `, ${user.firstName}` : ""}
                  </h2>
                  <p className="text-sm text-muted-foreground">Tell us about your athletic program</p>
                </div>
              </div>

              <div className="space-y-5">
                <div>
                  <Label htmlFor="institutionName" className="text-sm font-semibold text-card-foreground">
                    Institution Name <span className="text-signal-red">*</span>
                  </Label>
                  <Input
                    id="institutionName"
                    className="mt-1.5 h-11 bg-background"
                    placeholder="e.g. Westfield University"
                    value={institutionName}
                    onChange={(e) => setInstitutionName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleStep1Next()}
                    autoFocus
                  />
                </div>

                <div>
                  <Label htmlFor="division" className="text-sm font-semibold text-card-foreground">
                    Division / Level
                  </Label>
                  <select
                    id="division"
                    value={division}
                    onChange={(e) => setDivision(e.target.value)}
                    className="mt-1.5 w-full h-11 rounded-md border border-input bg-background px-3 text-sm text-foreground ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  >
                    <option value="">Select division…</option>
                    {DIVISIONS.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="conference" className="text-sm font-semibold text-card-foreground">
                    Conference <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="conference"
                    className="mt-1.5 h-11 bg-background"
                    placeholder="e.g. Big Ten, SEC, Pac-12…"
                    value={conference}
                    onChange={(e) => setConference(e.target.value)}
                  />
                </div>
              </div>

              <Button
                onClick={handleStep1Next}
                disabled={!institutionName.trim()}
                className="w-full mt-7 h-11 bg-navy hover:bg-navy/90 text-white font-semibold shadow-md ring-1 ring-gold/30"
              >
                Continue <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <div className="space-y-5 animate-fade-in">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-foreground">Pick a starting point</h2>
              <p className="text-muted-foreground mt-1.5 text-sm">
                Start free in seconds, or pick a tier and our team will reach out within one business day.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {PLANS.map((plan) => {
                const Icon = plan.icon;
                const isSelected = selectedPlan === plan.id;
                return (
                  <button
                    type="button"
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`relative text-left rounded-2xl border bg-card p-5 cursor-pointer transition-all hover:shadow-md ${
                      isSelected
                        ? "border-gold ring-2 ring-gold/40 shadow-lg"
                        : "border-card-border hover:border-foreground/20"
                    }`}
                  >
                    {plan.badge && (
                      <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                        <Badge className="bg-gold text-gold-foreground text-[10px] font-bold tracking-wide uppercase px-2.5 py-0.5">
                          {plan.badge}
                        </Badge>
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className={`h-4.5 w-4.5 ${isSelected ? "text-gold" : "text-muted-foreground"}`} />
                      <span className="font-bold text-card-foreground">{plan.name}</span>
                      {isSelected && <CheckCircle2 className="h-4 w-4 text-field-green ml-auto" />}
                    </div>
                    <div className="mb-1">
                      <span className={`text-base font-bold ${plan.id === "free" ? "text-field-green" : "text-card-foreground"}`}>{plan.priceLabel}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-4 min-h-[2.5rem]">{plan.description}</p>
                    <ul className="space-y-1.5">
                      {plan.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-xs text-card-foreground/80">
                          <CheckCircle2 className="h-3.5 w-3.5 text-field-green flex-shrink-0 mt-0.5" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </button>
                );
              })}
            </div>

            {error && (
              <div className="text-sm text-signal-red bg-signal-red/10 border border-signal-red/30 rounded-lg px-4 py-3">
                {error}
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1 h-11">
                Back
              </Button>
              <Button
                onClick={handleProvision}
                disabled={loading}
                className="flex-[2] h-11 bg-navy hover:bg-navy/90 text-white font-semibold shadow-md ring-1 ring-gold/30"
              >
                {loading
                  ? "Setting up your account…"
                  : selectedPlan === "free"
                  ? "Start Free Trial →"
                  : "Start trial + request pricing →"}
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <Card className="shadow-xl border border-card-border bg-card animate-fade-in">
            <CardContent className="p-10 text-center">
              <div className="w-16 h-16 rounded-full bg-field-green-subtle ring-1 ring-field-green/30 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="h-8 w-8 text-field-green" />
              </div>
              <h2 className="text-2xl font-bold text-card-foreground mb-2">You're all set</h2>
              <p className="text-muted-foreground mb-3 text-sm">
                <span className="font-semibold text-card-foreground">{institutionName}</span> is provisioned on the Free trial — jump in and start exploring.
              </p>
              {(division || conference) && (
                <div className="flex flex-wrap items-center justify-center gap-2 mb-3">
                  {division && <Badge variant="muted" className="text-xs font-medium">{division}</Badge>}
                  {conference && <Badge variant="muted" className="text-xs font-medium">{conference}</Badge>}
                </div>
              )}
              {selectedPlan === "free" ? (
                <p className="text-muted-foreground text-xs mb-8">Your 14-day Track trial starts now.</p>
              ) : (
                <p className="text-muted-foreground text-xs mb-8">
                  Our team will reach out within one business day to scope your
                  {" "}<span className="font-semibold text-card-foreground">
                    {selectedPlan === "track" ? "Command" : selectedPlan === "draft" ? "Intelligence" : "Enterprise Plus"}
                  </span>{" "}
                  rollout and share pricing.
                </p>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8 text-left">
                {[
                  { icon: FileText, label: "Add contracts", desc: "Import or create coach contracts" },
                  { icon: Users, label: "Add coaches", desc: "Build your coaching staff roster" },
                  { icon: BarChart3, label: "Track compliance", desc: "Monitor insurance & risk items" },
                ].map(({ icon: Icon, label, desc }) => (
                  <div key={label} className="bg-muted/40 border border-card-border rounded-xl p-4 flex flex-col gap-1.5">
                    <div className="w-8 h-8 rounded-md bg-gold-subtle ring-1 ring-gold/30 flex items-center justify-center">
                      <Icon className="h-4 w-4 text-gold" />
                    </div>
                    <div className="text-sm font-semibold text-card-foreground">{label}</div>
                    <div className="text-xs text-muted-foreground">{desc}</div>
                  </div>
                ))}
              </div>

              <Button
                onClick={() => setLocation("/dashboard")}
                className="w-full h-12 bg-navy hover:bg-navy/90 text-white font-bold text-base shadow-md ring-1 ring-gold/30"
              >
                Go to Dashboard <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
